import {makeAutoObservable} from "mobx";
import history from '../authentication/History';
import {toast} from 'react-toastify';
import environment from '../environments';
import {apiFetch, notifyError} from '../api';

const parseJsonField = (value) => typeof value === "string" ? JSON.parse(value) : value;

const emptyOrder = () => ({checklist: [false, false, false, false, false, false, false, false], stickerList: []});

class OrderStore
{
    orderList = [];
    order = emptyOrder();
    /* ID of the record in order, so a different record never shows stale data */
    loadedId = null;

    start = 0;
    limit = 100;
    productOptions = [];
    clientOptions = [];
    orderTypeOptions = [
        {uuid: "0", value: "stickers", label: "Sticker"},
        {uuid: "1", value: "cards", label: "Karte"},
        {uuid: "2", value: "pendants", label: "3er Anhänger"}
    ];
    selectedClient = null;
    selectedProduct = null;
    selectedOrderType = this.orderTypeOptions[0].value;

    printable = false;
    printedStickers = [];
    printing = false;

    searchTerm = "";

    /* Endpoints */
    detailRoute = "/orders";
    dataEndpoint = environment.API_HOST + "/orders/list";
    searchEndpoint = environment.API_HOST + "/orders/search";

    /* Table configuration */
    sortBy = "orderid";
    sortOrder = "asc";
    tableColumns = [
        {name: "Auftragsnummer", field: "orderid", sortable: true},
        //{name: "Kundennummer", field: "customerid", sortable: true},
        {name: "Firma", field: "company", sortable: true},
        //{name: "Nachname", field: "lastname"},
        //{name: "Vorname", field: "firstname"},
        {name: "Kopien", field: "copies", sortable: true},
        {name: "Einheiten", field: "units", sortable: true},
        {name: "Sticker (Einzigartig)", field: "unique_stickers", sortable: true},
        {name: "Sticker (Gesamt)", field: "total_stickers", sortable: true},
        {name: "Sticker (Aktiviert)", field: "activestickers", sortable: true},
        {name: "Produktionsart", field: "type", type: "orderType", sortable: true},
        {name: "Produkt", field: "product_name", sortable: true}
    ]

    constructor()
    {
        makeAutoObservable(this)
    }

    getOrderList()
    {
        apiFetch(this.dataEndpoint + "?start=" + this.start + "&limit=" + this.limit + "&sortBy=" + this.sortBy +
            "&sortOrder=" + this.sortOrder)
            .then((json) =>
            {
                this.orderList = json.data
            })
            .catch((error) =>
            {
                notifyError(error);
                return error;
            })
    }

    searchOrder()
    {
        /* Start again on the first page */
        this.start = 0;

        if (this.searchTerm && this.searchTerm.length > 0)
        {
            const term = this.searchTerm;
            apiFetch(this.searchEndpoint + "?term=" + encodeURIComponent(term))
                .then((json) =>
                {
                    if (term === this.searchTerm)
                    {
                        this.orderList = json.data
                    }
                })
                .catch((error) =>
                {
                    notifyError(error);
                })

        } else
        {
            this.getOrderList();
        }
    }

    addOrder(clientId, productId, orderType)
    {
        if (clientId !== null && productId !== null && orderType !== null)
        {
            apiFetch(environment.API_HOST + "/orders/new?client=" + clientId + "&product=" + productId + "&type=" +
                orderType, {
                method: 'POST',
            })
                .then((json) =>
                {
                    history.push('/orders/' + json.data.id)
                })
                .catch((error) =>
                {
                    notifyError(error);
                })
        }
    }

    getOrder(id)
    {
        if (this.loadedId !== id)
        {
            this.order = emptyOrder();
            this.printedStickers = [];
            this.printable = false;
            this.loadedId = id;
        }

        apiFetch(environment.API_HOST + "/orders/" + id)
            .then((json) =>
            {
                /* Ignore responses for a record that is no longer open */
                if (id !== this.loadedId)
                {
                    return;
                }

                this.order = json.data[0]
                /* The API returns these JSON columns as strings, but defaults for new orders as arrays */
                this.printedStickers = parseJsonField(this.order.printed);
                this.order.checklist = parseJsonField(this.order.checklist);

                if (this.order.printed !== null || this.order.units === null || this.order.copies === null)
                {
                    this.printable = false;
                } else
                {
                    this.printable = true;
                }
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    saveOrder(id)
    {
        var payload = {
            clients_uuid: this.order.clients_uuid,
            products_uuid: this.order.products_uuid,
            copies: this.order.copies,
            units: this.order.units,
            checklist: JSON.stringify(this.order.checklist)
        }

        payload = JSON.stringify(payload);

        apiFetch(environment.API_HOST + "/orders/" + id, {
            method: 'PUT',
            headers: {
                "Content-Type": "application/json"
            },
            body: payload
        })
            .then((json) =>
            {
                toast.success("Auftrag erfolgreich gespeichert.");
                this.getOrder(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    deleteOrder(id)
    {
        apiFetch(environment.API_HOST + "/orders/" + id, {
            method: 'DELETE',
        })
            .then((json) =>
            {
                history.push('/orders')
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    printOrder(id)
    {
        this.printing = true;
        apiFetch(environment.API_HOST + "/orders/" + id + "/print", {
            method: 'PUT',
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((json) =>
            {
                toast.success("Auftrag wurde erfolgreich für den Druck vorbereitet.");
                this.printing = false;
                this.getOrder(id);
            })
            .catch((error) =>
            {
                notifyError(error);
                this.printing = false;
            })
    }

    printOrderAsCard(id)
    {
        this.printing = true;
        apiFetch(environment.API_HOST + "/orders/" + id + "/print-card", {
            method: 'PUT',
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((json) =>
            {
                toast.success("Auftrag wurde erfolgreich für den Kartendruck vorbereitet.");
                this.printing = false;
                this.getOrder(id);
            })
            .catch((error) =>
            {
                notifyError(error);
                this.printing = false;
            })
    }

    printOrderAsPendants(id)
    {
        this.printing = true;
        apiFetch(environment.API_HOST + "/orders/" + id + "/print-pendants", {
            method: 'PUT',
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((json) =>
            {
                toast.success("Auftrag wurde erfolgreich für den Druck der 3er-Anhänger vorbereitet.");
                this.printing = false;
                this.getOrder(id);
            })
            .catch((error) =>
            {
                notifyError(error);
                this.printing = false;
            })
    }

    sort(column)
    {
        /* Start again on the first page */
        this.start = 0;

        this.sortBy = column;

        if (this.sortOrder === 'asc')
        {
            this.sortOrder = 'desc';
        } else
        {
            this.sortOrder = 'asc';
        }

        this.getOrderList();
    }

    nextPage()
    {
        this.start = this.start + this.limit
        this.getOrderList();
    }

    previousPage()
    {
        if (this.start - this.limit >= 0)
        {
            this.start = this.start - this.limit
            this.getOrderList();
        }
    }

    loadOptions()
    {
        /* Clients */
        apiFetch(environment.API_HOST + "/clients/list")
            .then((json) =>
            {
                this.clientOptions = json.data
                this.selectedClient = this.clientOptions.length > 0 ? this.clientOptions[0].uuid : null;
            })
            .catch((error) =>
            {
                notifyError(error);
            })

        /* Products */
        apiFetch(environment.API_HOST + "/products/list")
            .then((json) =>
            {
                this.productOptions = json.data
                this.selectedProduct = this.productOptions.length > 0 ? this.productOptions[0].uuid : null;
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }
}

const orderStore = new OrderStore();
export default orderStore;
